import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { set, get } from 'lodash';
import { PROJECT_ASSESSMENT_STATUSES, PROJECT_ASSESSMENT_TOOLTIP, NOTIFICATION, PROJECT_TYPES } from '@/constants';
import { isValidNumericInputKey } from '@/helpers';
import { ProjectAssessmentDTO, ProjectAssessmentModelDTO, ProjectAssessmentPutDTO, UserDTO } from '@/models';
import {
  getActualEffort,
  getAddedValue,
  getAverageSatAndComplexityByAssessmentId,
  getProjectBaseValue
} from '@/services/project-drops';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import Tooltip from '@/components/common/tooltip/Tooltip.vue';
import RatingField from '@/components/common/rating-field/RatingField.vue';

enum PROJECT_ASSESSMENT_MODE {
  EDIT,
  VIEW
}

interface TooltipData {
  actualEffort: string;
  plannedEffort: string;
  key: string;
  sat: string;
  complexity: string;
  projectBaseValue: string;
  addedValue: string;
}

const buildProjectAssessmentFormData = (): ProjectAssessmentModelDTO => ({
  actualEffort: 0,
  key: 0,
  plannedEffort: 0,
  sat: 0,
  complexity: 0,
  finalDrops: 0
});

@Component({
  components: {
    tooltip: Tooltip,
    ConfirmationDialog,
    RatingField
  }
})
export default class ProjectAssessment extends Vue {
  @Prop({ default: false }) canEdit: boolean;
  @Prop() onStepConfirmed: () => void;

  private projectAssessmentFormData: ProjectAssessmentModelDTO = buildProjectAssessmentFormData();
  private currentUser: UserDTO;
  private mode = PROJECT_ASSESSMENT_MODE.VIEW;
  private saveLoading = false;
  private confirmLoading = false;
  private confirmationDialogShown = false;
  private dirtyCheckDialogShown = false;

  @Watch('mode')
  setIsProjectEditing(): void {
    this.$store.commit('project/setIsProjectEditing', this.mode === PROJECT_ASSESSMENT_MODE.EDIT);
  }

  get projectType(): string {
    return this.$store.getters['project/projectType'];
  }

  get projectAssessment(): ProjectAssessmentDTO {
    return this.$store.getters['project/projectAssessment'];
  }

  get isViewMode(): boolean {
    return this.mode === PROJECT_ASSESSMENT_MODE.VIEW;
  }

  get toolTipValue(): TooltipData {
    return {
      actualEffort: PROJECT_ASSESSMENT_TOOLTIP.ACTUAL_EFFORT,
      plannedEffort: PROJECT_ASSESSMENT_TOOLTIP.PLANNED_EFFORT,
      key: PROJECT_ASSESSMENT_TOOLTIP.KEY,
      sat: PROJECT_ASSESSMENT_TOOLTIP.SAT,
      complexity: PROJECT_ASSESSMENT_TOOLTIP.COMPLEXITY,
      projectBaseValue: PROJECT_ASSESSMENT_TOOLTIP.PROJECT_BASE_VALUE,
      addedValue: PROJECT_ASSESSMENT_TOOLTIP.ADDED_VALUE
    };
  }

  get isFinalProjectDropsShown(): boolean {
    if (this.isIndividualTrackingProject) {
      return true;
    }
    return (
      this.projectAssessment?.assessmentStatus === PROJECT_ASSESSMENT_STATUSES.PROJECT_EVALUATION ||
      this.projectAssessment?.assessmentStatus === PROJECT_ASSESSMENT_STATUSES.PEER_EVALUATION
    );
  }

  get isIndividualTrackingProject(): boolean {
    return this.$store.getters['project/projectType'] === PROJECT_TYPES.INDIVIDUAL_TRACKING;
  }

  get actualEffort(): number {
    return this.isIndividualTrackingProject
      ? 0
      : getActualEffort(
          this.projectType,
          this.projectAssessmentFormData.plannedEffort,
          this.projectAssessment.userEngagements
        );
  }

  get complexityValue(): number {
    return +this.projectAssessment.complexity;
  }

  setComplexityValue(value: string): void {
    const numericValue = +value;
    if (numericValue >= 1 && numericValue <= 5) {
      this.projectAssessmentFormData.complexity = numericValue;
    }
  }

  get satValue(): number {
    return +this.projectAssessment.sat;
  }

  setSatValue(value: string): void {
    const numericValue = +value;
    if (numericValue >= 1 && numericValue <= 5) {
      this.projectAssessmentFormData.sat = numericValue;
    }
  }

  get projectBaseValue(): number {
    const data = this.isViewMode ? this.projectAssessment : this.projectAssessmentFormData;
    return getProjectBaseValue(data);
  }

  get addedValue(): number {
    const data = this.isViewMode ? this.projectAssessment : this.projectAssessmentFormData;
    const averageSatAndComplexity = this.$store.getters['project/averageSatAndComplexity'];
    const { averageComplexity, averageSat } = getAverageSatAndComplexityByAssessmentId(
      averageSatAndComplexity,
      this.projectAssessment.id
    );
    const { actualEffort } = this;
    return getAddedValue(data, averageComplexity, averageSat, actualEffort);
  }

  get finalProjectDrops(): number {
    return Math.round(this.projectBaseValue + this.addedValue);
  }

  get isConfirmBtnDisabled(): boolean {
    const data = this.isViewMode ? this.projectAssessment : this.projectAssessmentFormData;
    const dropsEntered = !this.finalProjectDrops || !data.sat || !data.complexity;
    return this.saveLoading || this.confirmLoading || dropsEntered;
  }

  changeToEditMode(): void {
    this.mode = PROJECT_ASSESSMENT_MODE.EDIT;
  }

  changeToViewMode(): void {
    this.mode = PROJECT_ASSESSMENT_MODE.VIEW;
  }

  isDirty(): boolean {
    const { projectAssessment, projectAssessmentFormData } = this;
    return (
      this.isDifferent(projectAssessment.key, +projectAssessmentFormData.key) ||
      this.isDifferent(projectAssessment.plannedEffort, +projectAssessmentFormData.plannedEffort) ||
      this.isDifferent(projectAssessment.sat, +projectAssessmentFormData.sat) ||
      this.isDifferent(projectAssessment.complexity, +projectAssessmentFormData.complexity)
    );
  }

  isDifferent(value1: number, value2: number): boolean {
    if (!value1 && !value2) {
      return false;
    }
    return value1 !== value2;
  }

  cancel(): void {
    if (this.isDirty()) {
      this.dirtyCheckDialogShown = true;
    } else {
      this.onDirtyDialogConfirmed();
    }
  }

  onDirtyDialogConfirmed(): void {
    this.dirtyCheckDialogShown = false;
    this.changeToViewMode();
    this.buildFormData();
  }

  updateButtonsLoading(confirm: boolean): void {
    if (confirm) {
      this.confirmLoading = !this.confirmLoading;
    } else {
      this.saveLoading = !this.saveLoading;
    }
  }

  async onConfirm(confirm: boolean): Promise<void> {
    const projectAssessPutData: ProjectAssessmentPutDTO = {
      id: this.projectAssessment.id,
      projectId: parseInt(this.$route.params.id),
      projectAssessmentData: { ...this.projectAssessmentFormData, finalDrops: this.finalProjectDrops },
      confirm
    } as ProjectAssessmentPutDTO;
    this.updateButtonsLoading(confirm);
    try {
      await this.$store.dispatch('project/asyncUpdateProjectAssessment', projectAssessPutData);
      this.updateButtonsLoading(confirm);
      this.changeToViewMode();
      if (confirm) {
        this.confirmationDialogShown = false;
        const data = {
          showing: true,
          type: NOTIFICATION.TYPE.success,
          title: NOTIFICATION.TEXT.confirmProjectEvaluationSuccess,
          content: [],
          icon: NOTIFICATION.ICON.success,
          multiline: false,
          height: 0
        };
        this.$store.commit('notification/setNotification', data);
        this.$emit('onStepConfirmed');
      }
    } catch (e) {
      this.updateButtonsLoading(confirm);
      let text;
      if (confirm) {
        this.confirmationDialogShown = false;
        text = NOTIFICATION.TEXT.confirmProjectEvaluationFailed;
      } else {
        text = NOTIFICATION.TEXT.saveProjectEvaluationFailed;
      }
      const data = {
        showing: true,
        type: NOTIFICATION.TYPE.error,
        title: text,
        content: [],
        icon: NOTIFICATION.ICON.error,
        multiline: false,
        height: 0
      };
      this.$store.commit('notification/setNotification', data);
    }
  }

  buildFormData(): void {
    Object.keys(this.projectAssessmentFormData).forEach(key => {
      set(this.projectAssessmentFormData, key, get(this.projectAssessment, key) || 0);
    });
  }

  async created(): Promise<void> {
    this.currentUser = this.$store.getters['app/currentUser'];
    this.buildFormData();
  }

  get confirmMessage(): string {
    const isProjectHasOneMember = this.$store.getters['project/userEngagements'].length === 1;
    return isProjectHasOneMember
      ? 'Once the project pollens is confirmed, the results will be sent out.'
      : 'Once the project pollens is confirmed, the evaluation process will continue to the next step. You will not be able to edit the Project Assessment details after this point.';
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!isValidNumericInputKey(event)) {
      event.preventDefault();
    }
  }
}
